import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  Paper,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Home as HomeIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  ContactMail as ContactIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Logout as LogoutIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { useNavigate } from 'react-router-dom';
import { 
  studentService, 
  periodService, 
  timetableService, 
  feedbackService,
  announcementService,
  activityService,
} from '../services/apiService';
import { formatDate, getRelativeTime } from '../utils/dateFormat';
import { getAvatarColor, getInitials } from '../utils/avatar';
import { showToast } from '../utils/toast';

// Import child components
import ChildrenList from './components/ChildrenList';
import ChildOverview from './components/ChildOverview';
import TodayClasses from './components/TodayClasses';
import FeeTransactions from './components/FeeTransactions';
import AttendanceView from './components/AttendanceView';
import MarksView from './components/MarksView';
import SubjectsView from './components/SubjectsView';
import AnnouncementsView from './components/AnnouncementsView';
import HolidaysView from './components/HolidaysView';
import EventsView from './components/EventsView';
import SettingsView from './components/SettingsView';
import ContactUs from './components/ContactUs';
import ComplaintsView from './components/ChatWithTeacher';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const ParentPortalApp = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const { themeColor } = useAppConfig();
  
  // Navigation state
  const [currentView, setCurrentView] = useState('home'); // 'home', 'mychild', 'child', 'settings', 'contact'
  const [selectedChild, setSelectedChild] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  
  // Logout confirmation dialog state
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  
  // Home page view state
  const [homeViewSection, setHomeViewSection] = useState('announcements'); // 'announcements', 'holidays', 'events' - default shows announcements
  
  // Data state
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Home page data (school-wide)
  const [schoolAnnouncements, setSchoolAnnouncements] = useState([]);
  const [schoolHolidays, setSchoolHolidays] = useState([]);
  const [schoolEvents, setSchoolEvents] = useState([]);
  const [loadingHomeData, setLoadingHomeData] = useState(false);
  
  // Child details data
  const [childDetails, setChildDetails] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [todayAttendanceStatus, setTodayAttendanceStatus] = useState('Not Marked');
  const [marks, setMarks] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [todayRoutine, setTodayRoutine] = useState({ 
    status: 'loading', 
    entries: [], 
    message: '', 
    date: '' 
  });
  const [selectedExamTab, setSelectedExamTab] = useState(0);
  const [feeTransactions, setFeeTransactions] = useState([]);
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);
  const childUnreadCountsRef = useRef({});

  // Load today's routine
  const loadTodayRoutine = useCallback(async (studentRecord, holidaysList) => {
    if (!studentRecord?.class_id) {
      setTodayRoutine({ status: 'error', message: 'Class information not available.', entries: [], date: '' });
      return;
    }

    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10);
    const dayIndex = today.getDay();

    if (dayIndex === 0) {
      setTodayRoutine({
        status: 'holiday',
        message: 'Today is Sunday. No classes scheduled.',
        entries: [],
        date: formattedDate,
      });
      return;
    }

    const isHoliday = (holidaysList || []).find((holiday) => {
      if (!holiday?.date) return false;
      const holidayDate = holiday.date.split(' ')[0];
      return holidayDate === formattedDate;
    });

    if (isHoliday) {
      setTodayRoutine({
        status: 'holiday',
        message: isHoliday.name ? `Today is ${isHoliday.name}.` : 'Today is a school holiday.',
        entries: [],
        date: formattedDate,
      });
      return;
    }

    setTodayRoutine({ status: 'loading', entries: [], message: '', date: formattedDate });

    let currentPeriods = periods;
    if (!currentPeriods || currentPeriods.length === 0) {
      try {
        const periodRes = await periodService.getAll();
        currentPeriods = periodRes.data || periodRes || [];
        setPeriods(currentPeriods);
      } catch (err) {
        setTodayRoutine({
          status: 'error',
          message: err?.response?.data?.error || err?.message || 'Failed to load periods.',
          entries: [],
          date: formattedDate,
        });
        return;
      }
    }

    const filteredPeriods = (currentPeriods || [])
      .filter((period) => !/break/i.test(period?.period_name || ''))
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

    try {
      const timetableRes = await timetableService.getAll({
        class_id: studentRecord.class_id,
        day_of_week: dayIndex,
      });
      const timetableData = (timetableRes.data || timetableRes || [])
        .filter((entry) => entry.period_id);
      
      const entryMap = new Map();
      timetableData.forEach((entry) => {
        entryMap.set(entry.period_id, entry);
      });

      const entries = filteredPeriods.map((period) => {
        const match = entryMap.get(period.id);
        return {
          period_id: period.id,
          period_name: period.period_name,
          start_time: period.start_time,
          end_time: period.end_time,
          subject_name: match?.subject_name || null,
          teacher_name: match?.teacher_name || null,
        };
      });

      const hasSubjects = entries.some((entry) => Boolean(entry.subject_name));
      setTodayRoutine({
        status: hasSubjects ? 'schedule' : 'empty',
        message: hasSubjects ? '' : 'No classes for today.',
        entries,
        date: formattedDate,
      });
    } catch (err) {
      setTodayRoutine({
        status: 'error',
        message: err?.response?.data?.error || err?.message || 'Failed to load today\'s classes.',
        entries: [],
        date: formattedDate,
      });
    }
  }, [periods]);

  // Fetch children
  const fetchChildren = useCallback(async () => {
    if (authLoading) return;
    
    const userId = user?.id || user?.user_id || (() => {
      try {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          const parsed = JSON.parse(savedUser);
          return parsed.id || parsed.user_id;
        }
      } catch (e) {
        console.error('Error parsing saved user:', e);
      }
      return null;
    })();
    
    if (!userId) {
      setLoading(false);
      setError('User not found. Please login again.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await studentService.getAll({ user_id: userId });
      
      if (response.success) {
        setChildren(response.data || []);
      } else {
        const errorMsg = response.error || response.message || 'Failed to fetch children';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching children:', err);
      setError(err.message || 'Failed to fetch children. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  // Fetch school-wide data for home page
  const fetchHomeData = useCallback(async () => {
    try {
      setLoadingHomeData(true);
      
      // Fetch announcements (audience: 'all' or 'all_parents')
      const announcementsRes = await announcementService.getAll({ audience: 'all' });
      const allAnnouncements = announcementsRes.data || announcementsRes || [];
      const parentAnnouncements = allAnnouncements.filter(a => 
        a.audience === 'all' || a.audience === 'all_parents'
      );
      setSchoolAnnouncements(parentAnnouncements); // Show all
      
      // Fetch activities (holidays and events)
      const activitiesRes = await activityService.getAll();
      const allActivities = activitiesRes.data || activitiesRes || [];
      const today = new Date().toISOString().split('T')[0];
      
      const upcomingHolidays = allActivities
        .filter(a => a.type === 'holiday' && a.date >= today)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setSchoolHolidays(upcomingHolidays);
      
      const upcomingEvents = allActivities
        .filter(a => a.type === 'event' && a.date >= today)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setSchoolEvents(upcomingEvents);
    } catch (err) {
      console.error('Error fetching home data:', err);
      showToast.error('Failed to load school information');
    } finally {
      setLoadingHomeData(false);
    }
  }, []);

  // Fetch unread feedback count for the selected child only
  const fetchUnreadFeedbackCount = useCallback(async (specificChildId = null) => {
    const childIdToCount = specificChildId || selectedChild?.id;
    
    if (!childIdToCount) {
      setUnreadFeedbackCount(0);
      return;
    }

    try {
      const response = await feedbackService.getAll({ 
        student_id: childIdToCount
      });
      const feedbackData = response.data || [];
      const unreadCount = feedbackData.filter(f => !f.is_read || f.is_read === 0 || f.is_read === false).length;
      childUnreadCountsRef.current[childIdToCount] = unreadCount;
      setUnreadFeedbackCount(unreadCount);
    } catch (err) {
      console.error(`Error fetching feedback for child ${childIdToCount}:`, err);
      setUnreadFeedbackCount(0);
    }
  }, [selectedChild]);

  // Update unread count when selected child changes
  useEffect(() => {
    if (selectedChild?.id) {
      fetchUnreadFeedbackCount(selectedChild.id);
    } else {
      setUnreadFeedbackCount(0);
    }
  }, [selectedChild?.id, fetchUnreadFeedbackCount]);

  // Fetch child details
  const fetchChildDetails = useCallback(async (child) => {
    if (!child) return;
    
    try {
      setLoadingDetails(true);
      setSelectedChild(child);
      setCurrentView('child');
      setActiveSection(null);

      const response = await studentService.getDetails(child.id);
      
      if (response.success) {
        const data = response.data;
        setChildDetails(data.student);
        setAttendance(data.attendance || []);
        setAttendanceStats(data.attendance_stats || null);
        setTodayAttendanceStatus(data.today_attendance_status || 'Not Marked');
        setMarks(data.marks || []);
        setSubjects(data.subjects || []);
        setAnnouncements(data.announcements || []);
        setHolidays(data.holidays || []);
        setEvents(data.events || []);
        setFeeTransactions(data.fee_transactions || []);
        setSelectedExamTab(0);

        await loadTodayRoutine(data.student, data.holidays || []);
      } else {
        const errorMsg = response.error || response.message || 'Failed to load child details';
        showToast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching child details:', err);
      showToast.error('Failed to load child details');
    } finally {
      setLoadingDetails(false);
    }
  }, [loadTodayRoutine]);

  useEffect(() => {
    if (!authLoading) {
      fetchChildren();
      if (currentView === 'home') {
        fetchHomeData();
      }
    }
  }, [fetchChildren, authLoading, currentView, fetchHomeData]);

  const handleBack = () => {
    if (activeSection) {
      setActiveSection(null);
    } else if (currentView === 'child') {
      setCurrentView('mychild');
      setSelectedChild(null);
      setActiveSection(null);
    } else if (currentView === 'mychild') {
      setCurrentView('home');
      setSelectedChild(null);
      setActiveSection(null);
    } else if (currentView === 'settings' || currentView === 'contact') {
      setCurrentView('home');
    }
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    setLogoutDialogOpen(false);
    await logout();
    navigate('/landing', { replace: true });
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const renderContent = () => {
    // Home view - School-wide content
    if (currentView === 'home') {
      return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Tabs for filtering */}
          <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={homeViewSection}
              onChange={(e, newValue) => setHomeViewSection(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: { xs: 80, sm: 120 },
                },
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Announcements
                    {schoolAnnouncements.length > 0 && (
                      <Chip 
                        label={schoolAnnouncements.length} 
                        size="small" 
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                } 
                value="announcements" 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Holidays
                    {schoolHolidays.length > 0 && (
                      <Chip 
                        label={schoolHolidays.length} 
                        size="small" 
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                } 
                value="holidays" 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Events
                    {schoolEvents.length > 0 && (
                      <Chip 
                        label={schoolEvents.length} 
                        size="small" 
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                } 
                value="events" 
              />
            </Tabs>
          </Box>

          {/* Content based on selected tab */}
          {homeViewSection === 'announcements' && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NotificationsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  Announcements
                </Typography>
              </Box>
              {loadingHomeData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : schoolAnnouncements.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No announcements at the moment
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Stack spacing={1.5}>
                  {schoolAnnouncements.map((announcement) => (
                    <Card
                      key={announcement.id}
                      sx={{
                        borderRadius: 2,
                        boxShadow: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                          {announcement.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {announcement.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getRelativeTime(announcement.created_at || announcement.sent_at)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {homeViewSection === 'holidays' && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarIcon sx={{ color: '#FF9800', fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  Upcoming Holidays
                </Typography>
              </Box>
              {loadingHomeData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : schoolHolidays.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No upcoming holidays
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Stack spacing={1.5}>
                  {schoolHolidays.map((holiday) => (
                    <Card
                      key={holiday.id}
                      sx={{
                        borderRadius: 2,
                        boxShadow: 2,
                        borderLeft: '4px solid',
                        borderColor: '#FF9800',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          {holiday.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(holiday.date)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {homeViewSection === 'events' && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EventIcon sx={{ color: '#9C27B0', fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  Upcoming Events
                </Typography>
              </Box>
              {loadingHomeData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : schoolEvents.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No upcoming events
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Stack spacing={1.5}>
                  {schoolEvents.map((event) => (
                    <Card
                      key={event.id}
                      sx={{
                        borderRadius: 2,
                        boxShadow: 2,
                        borderLeft: '4px solid',
                        borderColor: '#9C27B0',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          {event.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(event.date)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      );
    }

    // My Child view - List of children
    if (currentView === 'mychild') {
      return (
        <ChildrenList
          children={children}
          loading={loading}
          error={error}
          onChildSelect={fetchChildDetails}
        />
      );
    }

    // Child details view
    if (currentView === 'child' && selectedChild) {
      if (activeSection) {
        // Render specific section
        switch (activeSection) {
          case 'todayClasses':
            return (
              <TodayClasses
                routine={todayRoutine}
                onBack={() => setActiveSection(null)}
              />
            );
          case 'feeTransactions':
            return (
              <FeeTransactions
                transactions={feeTransactions}
                monthLabels={MONTH_LABELS}
                onBack={() => setActiveSection(null)}
              />
            );
          case 'attendance':
            return (
              <AttendanceView
                attendance={attendance}
                stats={attendanceStats}
                onBack={() => setActiveSection(null)}
              />
            );
          case 'marks':
            return (
              <MarksView
                marks={marks}
                selectedExamTab={selectedExamTab}
                onExamTabChange={setSelectedExamTab}
                onBack={() => setActiveSection(null)}
              />
            );
          case 'subjects':
            return (
              <SubjectsView
                subjects={subjects}
                onBack={() => setActiveSection(null)}
              />
            );
          case 'chat':
            return (
              <ComplaintsView
                child={selectedChild}
                childDetails={childDetails}
                onFeedbackRead={() => selectedChild?.id && fetchUnreadFeedbackCount(selectedChild.id)}
              />
            );
          default:
            return null;
        }
      } else {
        // Render child overview
        return (
          <ChildOverview
            child={selectedChild}
            childDetails={childDetails}
            attendanceStats={attendanceStats}
            todayAttendanceStatus={todayAttendanceStatus}
            todayRoutine={todayRoutine}
            feeTransactions={feeTransactions}
            marks={marks}
            subjects={subjects}
            loading={loadingDetails}
            onSectionSelect={setActiveSection}
            unreadFeedbackCount={unreadFeedbackCount}
          />
        );
      }
    }

    if (currentView === 'settings') {
      return <SettingsView onBack={() => {}} />;
    }

    if (currentView === 'contact') {
      return <ContactUs onBack={() => {}} />;
    }

    return null;
  };

  // Show back button when not on home page or when on a section
  const showBackButton = (currentView !== 'home' && currentView !== 'mychild') || (activeSection !== null);
  
  // Calculate bottom nav value based on current view
  const getBottomNavValue = useCallback(() => {
    if (currentView === 'home') return 0;
    if (currentView === 'mychild' || (currentView === 'child' && selectedChild)) return 1;
    if (currentView === 'contact') return 2;
    if (currentView === 'settings') return 3;
    if (currentView === 'logout') return 4;
    return 0;
  }, [currentView, selectedChild]);

  if (authLoading || (loading && currentView === 'mychild')) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Generate gradient background
  const primaryColor = themeColor || theme.palette.primary.main;
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };
  const rgb = hexToRgb(primaryColor);
  let gradientBackground = `linear-gradient(135deg, ${primaryColor} 0%, ${theme.palette.primary.dark || primaryColor} 100%)`;
  if (rgb) {
    const darkerR = Math.max(0, Math.floor(rgb.r * 0.7));
    const darkerG = Math.max(0, Math.floor(rgb.g * 0.7));
    const darkerB = Math.max(0, Math.floor(rgb.b * 0.7));
    const darkerShade = `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
    gradientBackground = `linear-gradient(135deg, ${primaryColor} 0%, ${darkerShade} 100%)`;
  }

  // Get page title
  const getPageTitle = () => {
    if (currentView === 'home') return ''; // No title for home
    if (currentView === 'mychild') return 'My Children';
    if (currentView === 'child') {
      if (activeSection) {
        switch (activeSection) {
          case 'todayClasses': return "Today's Classes";
          case 'feeTransactions': return 'Fee Transactions';
          case 'attendance': return 'Attendance';
          case 'marks': return 'Marks';
          case 'subjects': return 'Subjects';
          case 'chat': return 'Feedback';
          default: return 'Details';
        }
      }
      return selectedChild?.full_name || 'Child Details';
    }
    if (currentView === 'settings') return 'Settings';
    if (currentView === 'contact') return 'Contact Us';
    return 'Parent Portal';
  };

  // Determine header height based on current view
  const headerHeight = currentView === 'home' ? '15%' : (currentView === 'child' && selectedChild ? '30%' : '15%');

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      position: 'relative',
      background: gradientBackground,
      pb: '80px',
    }}>
      {/* Top Section - Title and Navigation */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: headerHeight,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: currentView === 'child' && selectedChild ? 'flex-start' : 'center',
          alignItems: 'center',
          px: 2,
          pt: currentView === 'child' && selectedChild ? 2 : 0,
        }}
      >
        {showBackButton && (
          <IconButton
            edge="start"
            sx={{ 
              position: 'absolute',
              top: 16,
              left: 16,
              color: 'white',
            }}
            onClick={handleBack}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        
        {currentView === 'child' && selectedChild ? (
          <Box sx={{ 
            width: '100%', 
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.5 },
          }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'center' },
                gap: { xs: 1.5, sm: 2.5 },
                backdropFilter: 'blur(10px)',
                p: { xs: 1.5, sm: 2 },
                //border: '1px solid rgba(255, 255, 255, 0.2)',
                width: '100%',
                maxWidth: '100%',
                marginTop: -2
              }}
            >
              {/* Avatar Section */}
              <Box
                sx={{
                  position: 'relative',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    bgcolor: getAvatarColor(selectedChild.full_name),
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: 28, sm: 36 },
                    fontWeight: 700,
                    color: '#ffffff',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                    border: '4px solid rgba(255, 255, 255, 0.3)',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: -4,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${getAvatarColor(selectedChild.full_name)}80, ${getAvatarColor(selectedChild.full_name)}40)`,
                      zIndex: -1,
                    },
                  }}
                >
                  {getInitials(selectedChild.full_name)}
                </Box>
              </Box>
              
              {/* Content Section */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                gap: { xs: 1, sm: 1.5 },
                width: '100%',
                alignItems: { xs: 'center', sm: 'flex-start' },
                textAlign: { xs: 'center', sm: 'left' },
              }}>
                {/* Name */}
                <Box>
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      fontWeight: 800, 
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      color: 'white',
                      mb: 0.25,
                      textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                  >
                    {selectedChild.full_name}
                  </Typography>
                  
                  {/* Class */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    <SchoolIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'rgba(255, 255, 255, 0.95)' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.95)',
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        fontWeight: 600,
                      }}
                    >
                      Class: {selectedChild.class_name}
                      {selectedChild.class_section && ` - ${selectedChild.class_section}`}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Attendance Cards */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  gap: { xs: 1.5, sm: 2 },
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}>
                  {/* Today's Attendance Card */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 0.25,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Chip
                      label={todayAttendanceStatus === 'Present' 
                        ? 'Present' 
                        : todayAttendanceStatus === 'Absent' 
                        ? 'Absent' 
                        : 'Pending'}
                      size="small"
                      sx={{
                        bgcolor: todayAttendanceStatus === 'Present' 
                          ? 'rgba(76, 175, 80, 1)' 
                          : todayAttendanceStatus === 'Absent' 
                          ? 'rgba(244, 67, 54, 1)' 
                          : 'rgb(204, 153, 0)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        height: { xs: 24, sm: 28 },
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        textAlign: 'center',
                      }}
                    >
                      Today's Status
                    </Typography>
                  </Box>
                  
                  {/* Total Attendance Card */}
                  {attendanceStats?.total_percentage !== undefined && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 0.25,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Chip
                        label={`${attendanceStats.total_percentage}%`}
                        size="small"
                        sx={{
                          bgcolor: attendanceStats.total_percentage > 75
                            ? 'rgba(76, 175, 80, 1)' // Green for > 75%
                            : attendanceStats.total_percentage > 50
                            ? 'rgb(204, 153, 0)' // Yellow for > 50% and <= 75%
                            : 'rgba(244, 67, 54, 1)', // Red for <= 50%
                          color: 'white',
                          fontWeight: 700,
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                          height: { xs: 24, sm: 28 },
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        }}
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          textAlign: 'center',
                        }}
                      >
                        Total Attendance
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : currentView === 'home' ? (
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            <Typography 
              variant="h5" 
              component="div" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: 'white',
                mb: 0.5,
              }}
            >
              Welcome, {user?.full_name || 'Parent'}!
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              Stay updated with school announcements, holidays, and events
            </Typography>
          </Box>
        ) : getPageTitle() ? (
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontWeight: 700, 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: 'white',
              textAlign: 'center',
            }}
          >
            {getPageTitle()}
          </Typography>
        ) : null}
      </Box>

      {/* Bottom Section - Content Box */}
      <Box
        sx={{
          position: 'absolute',
          top: headerHeight,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'background.paper',
          borderTopLeftRadius: { xs: '48px'},
          borderTopRightRadius: { xs: '48px'},
          boxShadow: '0 -12px 40px rgba(0,0,0,0.15), 0 -6px 20px rgba(0,0,0,0.1), 0 -2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Content Container */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            minHeight: 0,
            pt: 2,
            pb: 6,
          }}
        >
          {renderContent() || (
            <Box sx={{ p: 2, textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>

      {/* Bottom Navigation - Always visible */}
      <BottomNavigation
        value={getBottomNavValue()}
        onChange={(event, newValue) => {
          if (newValue === 0) {
            setCurrentView('home');
            setSelectedChild(null);
            setActiveSection(null);
            setHomeViewSection('announcements');
            fetchHomeData();
          } else if (newValue === 1) {
            setCurrentView('mychild');
            setSelectedChild(null);
            setActiveSection(null);
          } else if (newValue === 2) {
            setCurrentView('contact');
            setActiveSection(null);
          } else if (newValue === 3) {
            setCurrentView('settings');
            setActiveSection(null);
          } else if (newValue === 4) {
            handleLogoutClick();
          }
        }}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: 1000,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1), 0 -2px 8px rgba(0,0,0,0.06)',
          height: { xs: 70, sm: 75 },
          '& .MuiBottomNavigationAction-root': {
            color: 'text.secondary',
            minWidth: { xs: 50, sm: 60 },
            padding: { xs: '6px 8px', sm: '8px 12px' },
            transition: 'all 0.3s ease',
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 700,
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.15)',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))',
              },
            },
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'rgba(0,0,0,0.02)',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            fontWeight: 500,
            marginTop: 0.5,
            '&.Mui-selected': {
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              fontWeight: 700,
            },
          },
        }}
      >
        <BottomNavigationAction 
          label="Home" 
          icon={<HomeIcon />}
          sx={{
            '&.Mui-selected': {
              color: 'primary.main',
              bgcolor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
        <BottomNavigationAction 
          label="My child" 
          icon={<PersonIcon />}
          sx={{
            '&.Mui-selected': {
              color: 'primary.main',
              bgcolor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
        <BottomNavigationAction 
          label="Contact" 
          icon={<ContactIcon />}
          sx={{
            '&.Mui-selected': {
              color: 'primary.main',
              bgcolor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
        <BottomNavigationAction 
          label="Settings" 
          icon={<SettingsIcon />}
          sx={{
            '&.Mui-selected': {
              color: 'primary.main',
              bgcolor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
        <BottomNavigationAction 
          label="Logout" 
          icon={<LogoutIcon sx={{ color: 'error.main' }} />}
          sx={{
            color: 'error.main',
            '&:hover': {
              color: 'error.dark',
              bgcolor: 'rgba(244, 67, 54, 0.08)',
            },
            '&.Mui-selected': {
              color: 'error.main',
              bgcolor: 'rgba(244, 67, 54, 0.1)',
            },
            '& .MuiBottomNavigationAction-label': {
              color: 'error.main',
              '&.Mui-selected': {
                color: 'error.main',
              },
            },
          }}
        />
      </BottomNavigation>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to logout? You will need to login again to access the portal.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained" autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentPortalApp;
